// Drill-down GLOBAL — context + modal reutilizabil.
//
// Orice element „click-abil" din dashboard (un client, o categorie, o mașină,
// o lună) apelează `openDrill({type, valoare})`. Modalul calculează detaliul
// din fact-ul granular „comenzi" (via `derived.ts`) și îl afișează: comenzile
// aferente, marja, evoluția lunară — cu buton „Înapoi" (închide).

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import {
  Card,
  BarChart,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Badge,
} from "@tremor/react";
import { X, ArrowLeft, Layers } from "lucide-react";
import {
  comenziByClient,
  comenziByCategorie,
  comenziByMasina,
  comenziByLuna,
} from "@/data/derived";
import { getCostProductie, getIncarcareMasini } from "@/data/dataSource";
import type { ComandaRow } from "@/types";
import { eur, eurAxis, formatDataRO, formatLunaKey, numar, procent } from "@/lib/format";
import { labelCategorie } from "@/lib/labels";
import { TableScroll } from "@/components/ui/TableScroll";

export type DrillType = "client" | "categorie" | "masina" | "luna";
export interface DrillPayload {
  type: DrillType;
  valoare: string;
}

interface DrillContextValue {
  openDrill: (p: DrillPayload) => void;
  closeDrill: () => void;
}

const DrillContext = createContext<DrillContextValue | null>(null);

export function useDrilldown(): DrillContextValue {
  const ctx = useContext(DrillContext);
  if (!ctx) throw new Error("useDrilldown trebuie folosit în interiorul <DrilldownProvider>");
  return ctx;
}

export function DrilldownProvider({ children }: { children: ReactNode }) {
  const [payload, setPayload] = useState<DrillPayload | null>(null);
  const openDrill = useCallback((p: DrillPayload) => setPayload(p), []);
  const closeDrill = useCallback(() => setPayload(null), []);

  return (
    <DrillContext.Provider value={{ openDrill, closeDrill }}>
      {children}
      {payload && <DrillModal payload={payload} onClose={closeDrill} />}
    </DrillContext.Provider>
  );
}

// ─── Modalul ─────────────────────────────────────────────────────────────────
function DrillModal({ payload, onClose }: { payload: DrillPayload; onClose: () => void }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const { titlu, subtitlu, body } = buildDrillContent(payload);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-slate-900/50 p-3 backdrop-blur-sm sm:p-6"
      onClick={onClose}
    >
      <div
        className="my-4 w-full max-w-4xl rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 rounded-t-2xl border-b border-slate-200 bg-white/95 px-5 py-4 backdrop-blur">
          <div className="flex min-w-0 items-center gap-3">
            <button
              onClick={onClose}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Înapoi
            </button>
            <div className="min-w-0">
              <h2 className="truncate text-base font-semibold text-slate-900 sm:text-lg">{titlu}</h2>
              <p className="truncate text-xs text-slate-500">{subtitlu}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Închide"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4 px-5 py-5">{body}</div>
      </div>
    </div>
  );
}

// ─── Conținut per tip ────────────────────────────────────────────────────────
function buildDrillContent(payload: DrillPayload): {
  titlu: string;
  subtitlu: string;
  body: ReactNode;
} {
  if (payload.type === "luna") return drillLuna(payload.valoare);
  if (payload.type === "masina") return drillMasina(payload.valoare);
  if (payload.type === "categorie") return drillDimensiune("categorie", payload.valoare);
  return drillDimensiune("client", payload.valoare);
}

function sumar(rows: ComandaRow[]) {
  const venit = rows.reduce((s, c) => s + c.venit_eur, 0);
  const cost = rows.reduce((s, c) => s + c.cost_eur, 0);
  const marja = venit - cost;
  return {
    nr: rows.length,
    venit,
    cost,
    marja,
    marjaPct: venit > 0 ? (marja / venit) * 100 : 0,
    cantitate: rows.reduce((s, c) => s + c.cantitate_m2, 0),
  };
}

function KpiMini({ eticheta, valoare, sub }: { eticheta: string; valoare: string; sub?: string }) {
  return (
    <div className="min-w-0 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <p className="truncate text-xs font-medium text-slate-500">{eticheta}</p>
      <p className="tabular mt-1 truncate text-lg font-semibold text-slate-900">{valoare}</p>
      {sub && <p className="tabular mt-0.5 truncate text-xs text-slate-400">{sub}</p>}
    </div>
  );
}

function evolutieLunara(rows: ComandaRow[]) {
  const map = new Map<string, { venit: number; marja: number }>();
  for (const c of rows) {
    if (!map.has(c.luna_key)) map.set(c.luna_key, { venit: 0, marja: 0 });
    const a = map.get(c.luna_key)!;
    a.venit += c.venit_eur;
    a.marja += c.marja_eur;
  }
  return Array.from(map.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([luna, a]) => ({
      luna: formatLunaKey(luna),
      Venit: Math.round(a.venit),
      Marjă: Math.round(a.marja),
    }));
}

function tabelComenzi(rows: ComandaRow[], showClient = true) {
  const sortate = [...rows].sort((a, b) => b.data.localeCompare(a.data)).slice(0, 60);
  return (
    <Card className="ring-slate-200">
      <p className="mb-2 text-sm font-medium text-slate-700">
        Comenzi ({rows.length}){rows.length > 60 ? " · primele 60" : ""}
      </p>
      <TableScroll minWidth="46rem">
        <Table>
          <TableHead>
            <TableRow className="border-slate-200">
              <TableHeaderCell>Comandă</TableHeaderCell>
              <TableHeaderCell>Dată</TableHeaderCell>
              {showClient && <TableHeaderCell>Client</TableHeaderCell>}
              <TableHeaderCell>Categorie</TableHeaderCell>
              <TableHeaderCell className="text-right">Venit</TableHeaderCell>
              <TableHeaderCell className="text-right">Marjă</TableHeaderCell>
              <TableHeaderCell className="text-right">Marjă %</TableHeaderCell>
              <TableHeaderCell className="text-right">Întârziere</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortate.map((c) => (
              <TableRow key={c.id} className="border-slate-100">
                <TableCell className="font-mono text-xs text-slate-500">{c.id}</TableCell>
                <TableCell className="text-slate-600">{formatDataRO(c.data)}</TableCell>
                {showClient && <TableCell className="font-medium text-slate-800">{c.client}</TableCell>}
                <TableCell className="text-slate-600">{labelCategorie(c.categorie)}</TableCell>
                <TableCell className="text-right tabular text-slate-600">{eur(c.venit_eur, 0)}</TableCell>
                <TableCell className="text-right tabular font-medium text-slate-800">{eur(c.marja_eur, 0)}</TableCell>
                <TableCell className="text-right">
                  <Badge size="xs" color={c.marja_pct >= 25 ? "emerald" : c.marja_pct >= 10 ? "amber" : "rose"}>
                    {procent(c.marja_pct)}
                  </Badge>
                </TableCell>
                <TableCell className="text-right tabular">
                  {c.intarziere_zile > 0 ? (
                    <span className="text-rose-600">+{c.intarziere_zile} zile</span>
                  ) : (
                    <span className="text-emerald-600">la timp</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableScroll>
    </Card>
  );
}

function drillDimensiune(dim: "client" | "categorie", valoare: string) {
  const rows = dim === "client" ? comenziByClient(valoare) : comenziByCategorie(valoare);
  const s = sumar(rows);
  const eticheta = dim === "categorie" ? labelCategorie(valoare) : valoare;
  const evo = evolutieLunara(rows);
  return {
    titlu: eticheta,
    subtitlu:
      dim === "client"
        ? "Detaliu client — comenzi, marjă și evoluție (pe perioada filtrată)"
        : "Detaliu categorie — comenzi și evoluție (pe perioada filtrată)",
    body: (
      <>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <KpiMini eticheta="Venit total" valoare={eur(s.venit, 0)} sub={`${s.nr} comenzi`} />
          <KpiMini eticheta="Marjă" valoare={eur(s.marja, 0)} sub={procent(s.marjaPct)} />
          <KpiMini eticheta="Cost total" valoare={eur(s.cost, 0)} />
          <KpiMini eticheta="Cantitate" valoare={`${numar(s.cantitate, 0)} m²`} />
        </div>
        {evo.length > 1 && (
          <Card className="ring-slate-200">
            <p className="mb-2 text-sm font-medium text-slate-700">Evoluție lunară — venit vs. marjă</p>
            <BarChart
              className="mt-2 h-56 w-full"
              data={evo}
              index="luna"
              categories={["Venit", "Marjă"]}
              colors={["blue", "emerald"]}
              valueFormatter={eurAxis}
              yAxisWidth={56}
              noDataText="Fără date"
            />
          </Card>
        )}
        {tabelComenzi(rows, dim === "categorie")}
      </>
    ),
  };
}

function drillMasina(nume: string) {
  const incarcare = getIncarcareMasini()
    .filter((m) => m.nume_masina === nume)
    .sort((a, b) => a.luna_key.localeCompare(b.luna_key));
  const chart = incarcare.map((m) => ({
    luna: formatLunaKey(m.luna_key),
    "Încărcare %": m.incarcare_pct,
  }));
  const comenzi = comenziByMasina(nume);
  const s = sumar(comenzi);
  const medie = incarcare.length ? incarcare.reduce((sum, m) => sum + m.incarcare_pct, 0) / incarcare.length : 0;
  return {
    titlu: nume,
    subtitlu: "Detaliu mașină — încărcare lunară vs. capacitate + comenzi asociate",
    body: (
      <>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <KpiMini eticheta="Încărcare medie" valoare={procent(medie)} sub="vs. capacitate teoretică" />
          <KpiMini eticheta="Comenzi (perioadă)" valoare={String(s.nr)} />
          <KpiMini eticheta="Venit asociat" valoare={eur(s.venit, 0)} />
          <KpiMini eticheta="Cantitate" valoare={`${numar(s.cantitate, 0)} m²`} />
        </div>
        <Card className="ring-slate-200">
          <p className="mb-2 text-sm font-medium text-slate-700">Încărcare lunară</p>
          <BarChart
            className="mt-2 h-56 w-full"
            data={chart}
            index="luna"
            categories={["Încărcare %"]}
            colors={["amber"]}
            valueFormatter={(v) => `${numar(v, 0)}%`}
            yAxisWidth={44}
            showLegend={false}
            noDataText="Fără date"
          />
        </Card>
        <Card className="ring-slate-200">
          <TableScroll minWidth="36rem">
            <Table>
              <TableHead>
                <TableRow className="border-slate-200">
                  <TableHeaderCell>Lună</TableHeaderCell>
                  <TableHeaderCell className="text-right">Zile active</TableHeaderCell>
                  <TableHeaderCell className="text-right">Produs</TableHeaderCell>
                  <TableHeaderCell className="text-right">Capacitate</TableHeaderCell>
                  <TableHeaderCell className="text-right">Încărcare</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {incarcare.map((m) => (
                  <TableRow key={m.luna_key} className="border-slate-100">
                    <TableCell className="font-medium text-slate-700">{formatLunaKey(m.luna_key)}</TableCell>
                    <TableCell className="text-right tabular">{m.zile_active}</TableCell>
                    <TableCell className="text-right tabular">
                      {numar(m.cant_produsa, 0)} {m.unitate_capacitate}
                    </TableCell>
                    <TableCell className="text-right tabular text-slate-500">
                      {numar(m.capacitate_luna, 0)} {m.unitate_capacitate}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge size="xs" color={m.incarcare_pct >= 80 ? "rose" : m.incarcare_pct >= 50 ? "amber" : "blue"}>
                        {procent(m.incarcare_pct)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableScroll>
        </Card>
      </>
    ),
  };
}

function drillLuna(luna_key: string) {
  const cost = getCostProductie().find((c) => c.luna_key === luna_key);
  const comenzi = comenziByLuna(luna_key);
  const s = sumar(comenzi);
  const masiniLuna = getIncarcareMasini()
    .filter((m) => m.luna_key === luna_key)
    .sort((a, b) => b.incarcare_pct - a.incarcare_pct);
  const chartMasini = masiniLuna.map((m) => ({
    masina: m.nume_masina,
    "Încărcare %": m.incarcare_pct,
  }));
  return {
    titlu: formatLunaKey(luna_key, true),
    subtitlu: "Breakdown lună — cost producție, comenzi și încărcare mașini",
    body: (
      <>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <KpiMini
            eticheta="Cost /oră"
            valoare={cost ? eur(cost.cost_pe_ora_eur, 0) : "—"}
            sub={cost ? `${numar(cost.ore_lucrate, 0)} ore lucrate` : ""}
          />
          <KpiMini
            eticheta="Cost /unitate"
            valoare={cost ? eur(cost.cost_pe_unitate_eur, 0) : "—"}
            sub={cost ? `${numar(cost.unitati_m2, 0)} m² produse` : ""}
          />
          <KpiMini eticheta="Cost total lună" valoare={cost ? eur(cost.cost_total_eur, 0) : "—"} />
          <KpiMini eticheta="Comenzi / venit" valoare={String(s.nr)} sub={eur(s.venit, 0)} />
        </div>
        {chartMasini.length > 0 && (
          <Card className="ring-slate-200">
            <p className="mb-2 flex items-center gap-1.5 text-sm font-medium text-slate-700">
              <Layers className="h-4 w-4 text-slate-400" /> Încărcare mașini în lună
            </p>
            <BarChart
              className="mt-2 h-56 w-full"
              data={chartMasini}
              index="masina"
              categories={["Încărcare %"]}
              colors={["amber"]}
              layout="vertical"
              valueFormatter={(v) => `${numar(v, 0)}%`}
              yAxisWidth={140}
              showLegend={false}
              noDataText="Fără date"
            />
          </Card>
        )}
        {tabelComenzi(comenzi, true)}
      </>
    ),
  };
}
